import { useCallback, useEffect, useRef } from 'react';

const SCROLLABLE_OVERFLOW = /auto|scroll/;

function findScrollContainer(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node) {
    if (SCROLLABLE_OVERFLOW.test(getComputedStyle(node).overflowY)) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// The conversation thread is a column-reverse scroller: when a box inside it grows, the bottom
// edge stays put and the box's top is pushed up out of view. Scrolling by the drift on every
// resize keeps the top edge where the user clicked, so the growth goes downward instead.
export function useKeepTopEdgeInPlace<BoxElement extends HTMLElement>() {
  const boxRef = useRef<BoxElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const release = useCallback(() => {
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const hold = useCallback(() => {
    const box = boxRef.current;
    const scroller = box ? findScrollContainer(box) : null;
    if (!box || !scroller) {
      return;
    }

    release();
    const heldTop = box.getBoundingClientRect().top;
    const observer = new ResizeObserver(() => {
      scroller.scrollTop += box.getBoundingClientRect().top - heldTop;
    });
    observer.observe(box);
    observerRef.current = observer;
  }, [release]);

  useEffect(() => release, [release]);

  return { boxRef, hold, release };
}
